import type { ReactNode } from "react";
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { whatsappChatUrl } from "@/lib/whatsapp-chat-url";

/** Logo oficial en `public/brands/whatsapp.png` */
export const WHATSAPP_LOGO_SRC = "/brands/whatsapp.png";

type Variant = "button" | "icon" | "compact";

type Props = {
  phone: string | null | undefined;
  variant?: Variant;
  /** Evita que un click en una fila de tabla abra el drawer */
  stopRowClick?: boolean;
  className?: string;
  children?: ReactNode;
};

/** Botón pequeño que copia el número al portapapeles con feedback visual. */
export function CopyPhoneButton({
  phone,
  stopRowClick,
}: {
  phone: string | null | undefined;
  stopRowClick?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (!phone?.trim()) return null;

  async function handleCopy(e: React.MouseEvent) {
    if (stopRowClick) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(phone!.trim());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback silencioso
    }
  }

  return (
    <button
      type="button"
      title="Copiar número"
      aria-label="Copiar número de teléfono"
      onClick={(e) => void handleCopy(e)}
      className={css({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        w: "8",
        h: "8",
        rounded: "md",
        bg: copied ? "#f0fdf4" : "#f8fafc",
        borderWidth: "1px",
        borderColor: copied ? "#bbf7d0" : "#e2e8f0",
        color: copied ? "#166534" : "#64748b",
        cursor: "pointer",
        flexShrink: 0,
        fontSize: "14px",
        transition: "background 0.15s, color 0.15s, border-color 0.15s",
        _hover: { bg: "#f1f5f9", borderColor: "#cbd5e1" },
      })}
    >
      {copied ? "✓" : "⎘"}
    </button>
  );
}

export function WhatsAppChatLink({ phone, variant = "button", stopRowClick, className, children }: Props) {
  const href = whatsappChatUrl(phone);
  if (!href) return null;

  const base = css({
    textDecoration: "none",
    cursor: "pointer",
    flexShrink: 0,
  });

  const button = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "1",
    px: "3",
    py: "2",
    rounded: "lg",
    fontSize: "sm",
    fontWeight: "semibold",
    color: "#166534",
    borderWidth: "1px",
    borderColor: "#bbf7d0",
    bg: "#dcfce7",
    _hover: { bg: "#bbf7d0" },
  });

  const compact = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "1",
    px: "2",
    py: "1",
    rounded: "md",
    fontSize: "xs",
    fontWeight: "semibold",
    color: "#166534",
    bg: "#dcfce7",
    borderWidth: "1px",
    borderColor: "#bbf7d0",
    _hover: { bg: "#bbf7d0" },
  });

  const icon = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    w: "10",
    h: "10",
    p: "0",
    bg: "transparent",
    borderWidth: "0",
    rounded: "md",
    overflow: "visible",
    _hover: { opacity: 0.9, transform: "scale(1.04)" },
    transition: "opacity 0.15s ease, transform 0.15s ease",
  });

  const logoImg = css({
    display: "block",
    w: "full",
    h: "full",
    maxW: "40px",
    maxH: "40px",
    objectFit: "contain",
  });

  const logoImgSmall = css({
    display: "block",
    w: "4",
    h: "4",
    flexShrink: 0,
    objectFit: "contain",
  });

  const vClass = variant === "icon" ? icon : variant === "compact" ? compact : button;

  const defaultLabel =
    variant === "icon" ? (
      <img src={WHATSAPP_LOGO_SRC} alt="" className={logoImg} width={40} height={40} decoding="async" />
    ) : (
      <>
        <img src={WHATSAPP_LOGO_SRC} alt="" className={logoImgSmall} width={16} height={16} decoding="async" />
        WhatsApp
      </>
    );

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Abrir WhatsApp con el cliente"
      aria-label="Abrir WhatsApp con el cliente"
      className={cx(base, vClass, className)}
      onClick={stopRowClick ? (e) => e.stopPropagation() : undefined}
    >
      {children ?? defaultLabel}
    </a>
  );
}
